/**
 * LevelUpModal (iter78) — confetti celebration on DUPR promotion.
 *
 * On mount, polls `/api/me/level-up-pending`. If a promotion is pending,
 * shows a dismissible modal + a subtle confetti canvas. On close it POSTs
 * to `/me/level-up-pending/ack` so we never fire twice for the same
 * promotion. The nightly job (server-side) is what STAMPS the flag;
 * this component only surfaces + clears it.
 */
import { useEffect, useRef, useState } from "react";
import { Trophy, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import "./LevelUpModal.css";

export default function LevelUpModal() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== "student") return;
    let cancelled = false;
    api.get("/me/level-up-pending")
      .then((r) => { if (!cancelled) {
        const p = r.data?.level_up_pending;
        if (p) { setPending(p); setOpen(true); }
      }})
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Bare-bones confetti — no runtime dependency, just a canvas + gravity.
  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let rafId;
    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resize();
    const colours = ["#0BC6A4", "#FFD700", "#0057B8", "#FF4500", "#CCFF00"];
    const pieces = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 60,
      vx: (Math.random() - 0.5) * 3,
      vy: 2 + Math.random() * 3,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.2,
      size: 6 + Math.random() * 6,
      color: colours[Math.floor(Math.random() * colours.length)],
    }));
    let frames = 0;
    const tick = () => {
      frames++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.vy += 0.03;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      });
      if (frames < 300) rafId = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, [open]);

  const close = async () => {
    setOpen(false);
    try { await api.post("/me/level-up-pending/ack"); } catch { /* ignore */ }
  };

  if (!open || !pending) return null;
  return (
    <div className="levelup-backdrop" data-testid="levelup-modal" role="dialog" aria-modal="true">
      <canvas ref={canvasRef} className="levelup-confetti" aria-hidden />
      <div className="levelup-card">
        <button
          type="button" onClick={close}
          className="levelup-close" aria-label="Close"
          data-testid="levelup-modal-close"
        ><X className="w-4 h-4" /></button>
        <Trophy className="levelup-icon" strokeWidth={1.5} />
        <div className="levelup-eyebrow">LEVEL UP</div>
        <h2 className="levelup-title">
          You leveled up from{" "}
          <span className="from">{pending.from}</span> →{" "}
          <span className="to">{pending.to}</span>
        </h2>
        <p className="levelup-sub">
          That&apos;s a genuine promotion — the coach&apos;s ticks say you&apos;ve mastered the skills expected at
          Level {pending.from}. Onwards.
        </p>
        <button
          type="button" onClick={close}
          className="levelup-cta"
          data-testid="levelup-modal-cta"
        >Onwards to Level {pending.to} →</button>
      </div>
    </div>
  );
}
